import { createAsyncThunk } from '@reduxjs/toolkit';
import { errorGlobal, successGlobal } from '../reducers/notifications';
import { getAuthHeader } from '../../utils/tools';
import axios from 'axios';

axios.defaults.headers.post['Content-Type'] = 'application/json';

const buildConsentFormData = (values, file) => {
    const formData = new FormData();
    formData.append('name', values.name);
    formData.append('issuingAuthority', values.issuingAuthority?.value ?? '');
    formData.append('consentType', values.consentType);
    formData.append('decisionNumber', values.decisionNumber);
    formData.append('validFrom', new Date(values.validFrom).toISOString());
    formData.append('validUntil', new Date(values.validUntil).toISOString());
    formData.append('scopeType', values.scopeType);
    if (values.scopeType === 'branch') {
        formData.append('scopeBranch', values.scopeBranch);
    }
    formData.append('wasteCodes', JSON.stringify(values.wasteCodes.map(w => w.value)));
    formData.append('loadingCodes', JSON.stringify(values.loadingCodes.map(c => c.value)));
    if (file) {
        formData.append('attachment', file);
    }
    return formData;
};

export const fetchConsents = createAsyncThunk(
    'consent/fetchConsents',
    async(_, { dispatch }) => {
        try {
            const request = await axios.get(`/api/consent/fetch`, getAuthHeader());
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message || 'Nastala chyba. Skúste to prosím znova.'));
            throw error;
        }
    }
);

export const addConsent = createAsyncThunk(
    'consent/addConsent',
    async({ values, file }, { dispatch }) => {
        try {
            const formData = buildConsentFormData(values, file);
            const request = await axios.post(`/api/consent/create`, formData, getAuthHeader());

            dispatch(successGlobal('Súhlas bol úspešne vytvorený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Súhlas sa nepodarilo vytvoriť.'));
            throw error;
        }
    }
);

export const getConsentById = createAsyncThunk(
    'consent/getConsentById',
    async(consentId, { dispatch }) => {
        try {
            const request = await axios.get(`/api/consent/get-by-id`, {
                params: { consentId },
                ...getAuthHeader()
            });

            return request.data;
        } catch(error) {
            dispatch(errorGlobal('Súhlas sa nepodarilo načítať.'));
            throw error;
        }
    }
);

export const editConsent = createAsyncThunk(
    'consent/editConsent',
    async({ consentId, values, file }, { dispatch }) => {
        try {
            const formData = buildConsentFormData(values, file);
            formData.append('consentId', consentId);

            const request = await axios.patch(`/api/consent/edit`, formData, getAuthHeader());

            dispatch(successGlobal('Súhlas bol úspešne upravený.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Súhlas sa nepodarilo upraviť.'));
            throw error;
        }
    }
);

export const deleteConsent = createAsyncThunk(
    'consent/deleteConsent',
    async(consentId, { dispatch }) => {
        try {
            const request = await axios.delete(`/api/consent/delete`, {
                data: { consentId },
                ...getAuthHeader()
            });

            dispatch(successGlobal('Súhlas bol úspešne vymazaný.'));
            return request.data;
        } catch(error) {
            dispatch(errorGlobal(error.response?.data?.message ?? 'Súhlas sa nepodarilo vymazať.'));
            throw error;
        }
    }
);

export const downloadConsentAttachment = createAsyncThunk(
    'consent/downloadConsentAttachment',
    async({ consentId, filename }, { dispatch }) => {
        try {
            const response = await axios.get(`/api/consent/download`, {
                params: { consentId },
                responseType: 'blob',
                ...getAuthHeader()
            });

            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename ?? 'priloha';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch(error) {
            dispatch(errorGlobal('Prílohu sa nepodarilo stiahnuť.'));
            throw error;
        }
    }
);
